import { Injectable } from '@angular/core';
import { Observable, catchError, concat, defer, last, of, throwError } from 'rxjs';
import { CustomerService } from './customer.service';
import { UpdateVehiclePayload, VehicleService } from './vehicle.service';
import type { FamilyMemberEditChange } from '../shared/utils/family-member-edit.util';

/** Which call failed, and whether the other one had already gone through. */
export interface FamilyMemberEditFailure {
  step: 'customer' | 'vehicle';
  /** The vehicle was saved before the customer call failed; the card is out of date. */
  partial: boolean;
  cause: unknown;
}

/**
 * Saving the Family Plan tab's Edit Details form: the vehicle first (a plate another customer
 * holds is the common refusal, and then nothing has changed), then the customer.
 */
@Injectable({ providedIn: 'root' })
export class FamilyMemberEditService {
  constructor(
    private readonly customers: CustomerService,
    private readonly vehicles: VehicleService
  ) {}

  save(change: FamilyMemberEditChange): Observable<unknown> {
    const steps: Observable<unknown>[] = [];
    if (change.vehicle) {
      // The API takes a partial edit; the typed payload names every field of a full one.
      steps.push(
        defer(() =>
          this.vehicles.updateVehicle(change.vehicleId, change.vehicle as UpdateVehiclePayload)
        ).pipe(catchError(cause => throwError(() => failure('vehicle', false, cause))))
      );
    }
    if (change.customer) {
      steps.push(
        defer(() => this.customers.updateCustomer(change.customerId, change.customer)).pipe(
          catchError(cause => throwError(() => failure('customer', change.vehicle !== null, cause)))
        )
      );
    }
    return steps.length ? concat(...steps).pipe(last()) : of(null);
  }
}

const failure = (
  step: FamilyMemberEditFailure['step'],
  partial: boolean,
  cause: unknown
): FamilyMemberEditFailure => ({ step, partial, cause });
